import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ICinemaPalette } from '../palette';
import { MiniGameId } from './types';
import { IMiniGameEntry, kickerOf } from './catalog';
import { KEY_GUARD_ACTIVATE } from './keyGuard';
import { keepFocus } from './gameOptions';
import { getOverlayStyles } from './overlayStyles';
import GameSwitcher from './GameSwitcher';
import {
  MINI_GAME_CLOSE,
  MINI_GAME_ENTER,
  MINI_GAME_MUTE,
  MINI_GAME_UNMUTE,
} from './constants';

export interface IMiniGameStatus {
  percent: number;
  step: string;
  line: string | null;
}

interface IOverlayHeaderProps {
  classes: ReturnType<typeof getOverlayStyles>;
  palette: ICinemaPalette;
  game: IMiniGameEntry;
  switchable: IMiniGameEntry[];
  status: IMiniGameStatus;
  ready: boolean;
  bannerOpen: boolean;
  clockLine: ReactNode;
  muted: boolean;
  onEnter: () => void;
  onToggleMuted: () => void;
  onClose: () => void;
  onSwitch: (id: MiniGameId) => void;
}

const OverlayHeader = ({
  classes,
  palette,
  game,
  switchable,
  status,
  ready,
  bannerOpen,
  clockLine,
  muted,
  onEnter,
  onToggleMuted,
  onClose,
  onSwitch,
}: IOverlayHeaderProps) => (
  <Box sx={classes.header}>
    <Box sx={classes.titleBlock}>
      <Typography
        component="span"
        sx={classes.kicker}
        data-testid="myta-minigame-kicker"
      >
        {kickerOf(game)}
      </Typography>
      <Typography
        component="h2"
        sx={classes.title}
        data-testid="myta-minigame-title"
      >
        {game.title}
      </Typography>
    </Box>
    {switchable.length > 1 ? (
      <GameSwitcher
        palette={palette}
        games={switchable}
        activeId={game.id}
        onSwitch={onSwitch}
      />
    ) : null}
    <Box sx={classes.status} data-testid="myta-minigame-status">
      <Box sx={[classes.statusDot, ready && classes.statusDotStatic]} />
      <Box sx={classes.statusText}>
        <Typography component="span" sx={classes.statusStep}>
          {status.step}
        </Typography>
        {ready ? (
          !bannerOpen && clockLine ? (
            <Typography component="span" sx={classes.statusLine}>
              {clockLine}
            </Typography>
          ) : null
        ) : status.line ? (
          <Typography
            component="span"
            sx={classes.statusLine}
            data-testid="myta-minigame-eta"
          >
            {status.line}
          </Typography>
        ) : null}
      </Box>
      {ready && !bannerOpen ? (
        <Box
          component="button"
          type="button"
          sx={classes.headerCta}
          onClick={onEnter}
          {...KEY_GUARD_ACTIVATE}
          data-testid="myta-minigame-header-enter"
        >
          {MINI_GAME_ENTER}
          <ArrowForwardIcon />
        </Box>
      ) : (
        <Box sx={classes.statusPct} data-testid="myta-minigame-percent">
          {status.percent}
          <small>%</small>
        </Box>
      )}
    </Box>
    <Box sx={classes.actions}>
      <Box
        component="button"
        type="button"
        sx={classes.iconBtn}
        onMouseDown={keepFocus}
        onClick={onToggleMuted}
        title={muted ? MINI_GAME_UNMUTE : MINI_GAME_MUTE}
        aria-label={muted ? MINI_GAME_UNMUTE : MINI_GAME_MUTE}
        aria-pressed={muted}
        data-testid="myta-minigame-mute"
      >
        {muted ? <VolumeOffOutlinedIcon /> : <VolumeUpOutlinedIcon />}
      </Box>
      <Box
        component="button"
        type="button"
        sx={classes.iconBtn}
        onMouseDown={keepFocus}
        onClick={onClose}
        title={MINI_GAME_CLOSE}
        aria-label={MINI_GAME_CLOSE}
        data-testid="myta-minigame-close"
      >
        <CloseIcon />
      </Box>
    </Box>
    <Box
      sx={classes.streak}
      style={{ width: `${Math.max(0, Math.min(100, status.percent))}%` }}
    />
  </Box>
);

export default OverlayHeader;
